import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SocialFooter from '../components/SocialFooter';
import { EnvelopeSimple, Lock, GoogleLogo, ArrowLeft } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success('Welcome back!');
      navigate(data.user.onboarding_completed ? '/dashboard' : '/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center p-6 relative overflow-hidden hexagon-bg"
      data-testid="login-screen"
    >
      {/* Centered watermark - more visible */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url(${LOGO_URL})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '55%',
        }}
      />

      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 blur-[100px] rounded-full" />

      <div className="max-w-md w-full relative z-10">
        <button
          onClick={() => navigate('/welcome')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft weight="bold" className="w-5 h-5" />
          Back
        </button>

        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome Back
          </h1>
          <p className="text-muted-foreground">Continue your cardio journey</p>
        </div>

        <Button
          onClick={handleGoogleLogin}
          variant="outline"
          className="w-full border-[#D4AF37]/30 text-foreground rounded-xl py-6 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all mb-6"
          data-testid="google-login-btn"
        >
          <GoogleLogo weight="bold" className="w-5 h-5 mr-3" />
          Continue with Google
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-4 text-muted-foreground">Or continue with email</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-muted-foreground">Email</Label>
            <div className="relative">
              <EnvelopeSimple className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="pl-12 bg-card border-border rounded-xl py-6 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]" required data-testid="email-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-muted-foreground">Password</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-12 bg-card border-border rounded-xl py-6 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]" required data-testid="password-input" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-full py-6 hover:bg-[#F3E5AB] transition-all gold-glow gold-glow-hover mt-6" data-testid="submit-btn">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <p className="text-center mt-6 text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="register-link">Create one</Link>
        </p>

        <SocialFooter />
      </div>
    </div>
  );
};

export default Login;
